import React, { useEffect, useMemo } from 'react';
import DateFilter from './date-filter';
import LoaderFull from '../shared/loader-full-screen';
import { useFetchSecondDashboardData } from './hooks';
import styles from '../shared/common.module.css';
import { Pie, Bar } from 'react-chartjs-2';
import {
	Chart as ChartJS,
	ArcElement,
	Tooltip,
	Legend,
	CategoryScale,
	LinearScale,
	BarElement,
} from 'chart.js';
import { decodeName } from '../utils';
import {
	createSearchParams,
	useNavigate,
	useSearchParams,
} from 'react-router-dom';

ChartJS.register(
	ArcElement,
	Tooltip,
	Legend,
	CategoryScale,
	LinearScale,
	BarElement
);

const CHART_COLORS = [
	'#4472C4',
	'#ED7D31',
	'#A5A5A5',
	'#FFC000',
	'#5B9BD5',
	'#70AD47',
	'#264478',
	'#9E480E',
	'#636363',
	'#997300',
];

function displayName(value) {
	if (value == null || value === '') return '—';
	return value;
}

function mergeStatusCounts(statusMap = {}) {
	const merged = {};
	Object.entries(statusMap).forEach(([status, count]) => {
		const key = status || 'Unknown';
		const normalized = Object.keys(merged).find(
			(k) => k.toLowerCase() === key.toLowerCase()
		);
		const label = normalized || key;
		merged[label] = (merged[label] || 0) + (Number(count) || 0);
	});
	return merged;
}

function aggregateVendorStatus(rows = []) {
	const map = new Map();

	rows.forEach((row) => {
		const category = row.category_name ?? '—';
		const client = row.client_name ?? '—';
		const vendor = row.vendor_name ?? '—';
		const key = `${category}||${client}||${vendor}`;
		const status = (row.disp_status || '').toLowerCase();
		const count = Number(row.count) || 0;

		if (!map.has(key)) {
			map.set(key, {
				category_name: category,
				client_name: client,
				vendor_name: vendor,
				invoices: 0,
				completed: 0,
				trained: 0,
			});
		}

		const entry = map.get(key);
		entry.invoices += count;
		if (status === 'completed') entry.completed += count;
		if (status === 'trained') entry.trained += count;
	});

	return Array.from(map.values());
}



function Dashboard2() {
	const get_dashboard_hook = useFetchSecondDashboardData();
	const data = get_dashboard_hook.data || {};
	const hasData = Boolean(get_dashboard_hook.data);
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();


	function handleNavigate(obj) {
		console.log('obj', obj);
		const encodedClientName = encodeURIComponent(
			decodeName(obj.client_name)
		);
		const encodedVendorName = encodeURIComponent(obj.vendor_name);
		const encodeCategoryName = encodeURIComponent(obj.category_name);
		navigate({
			pathname: '/my-invoices',
			search: createSearchParams({
				client_id: obj.client_id,
				client_name: encodedClientName,
				client_list_page_no: 1,
				client_list_sort_key: '',
				client_list_sort_order: '',

				vendor_id: obj.vendor_id,
				vendor_name: encodedVendorName,
				vendor_list_page_no: 1,
				vendor_list_sort_key: '',
				vendor_list_sort_order: '',
				category_name: encodeCategoryName,
				category_id: obj.category_id,

				invoice_list_page_no: 1,
				invoice_list_sort_key: '',
				invoice_list_sort_order: '',
			}).toString(),
		});
	}

	function handleNavigateToContracts(obj) {
		const encodedClientName = encodeURIComponent(
			decodeName(obj.client_name)
		);
		const encodedVendorName = encodeURIComponent(obj.vendor_name);
		const encodeCategoryName = encodeURIComponent(obj.category_name);
		navigate({
			pathname: '/my-contracts',
			search: createSearchParams({
				client_id: obj.client_id,
				client_name: encodedClientName,
				client_list_page_no: 1,
				client_list_sort_key: '',
				client_list_sort_order: '',

				vendor_id: obj.vendor_id,
				vendor_name: encodedVendorName,
				vendor_list_page_no: 1,
				vendor_list_sort_key: '',
				vendor_list_sort_order: '',
				category_name: encodeCategoryName,
				category_id: obj.category_id,

				contract_list_page_no: 1,
				contract_list_sort_key: '',
				contract_list_sort_order: '',
			}).toString(),
		});
	}

	return (
		<div className="grow fontInter">
			<div className="px-6 py-4 mb-4">
				<div className="pb-4">
					<h1 className="text-lg font-black text-primary">Dashboard</h1>
					<p className="text-xs text-gray-500">
						Total data of Clients, Vendors, Invoices, Contracts
					</p>
				</div>
				<DateFilter get_dashboard_hook={get_dashboard_hook} />
			</div>

			{get_dashboard_hook.isPending ? (
				<LoaderFull />
			) : hasData ? (
				<div className="px-6 pb-8 space-y-4">
					

					<SummaryCards data={data.kpi0} />

					<div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
						<StuckFilesTable data={data.kpi5} />
						<OverallVendorStatusTable handleNavigate={handleNavigate} handleNavigateToContracts={handleNavigateToContracts} data={data.kpi1} />
					</div>

					<div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
						<VendorsByStatusPie data={data.kpi3} />
						<InvoicesByStatusBar data={data.kpi4} />
						<InvoiceStatusTable data={data.kpi2} />
					</div>
				</div>
			) : null}
		</div>
	);
}

export default Dashboard2;

function SummaryCards({ data }) {
	const cards = [
		{ label: 'Expense Area', value: data?.total_categories },
		{ label: 'Client', value: data?.total_client },
		{ label: 'Vendor', value: data?.total_vendor },
		{ label: 'Invoices', value: data?.total_invoices },
	];

	return (
		<div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
			{cards.map((card) => (
				<div
					key={card.label}
					className={`${styles.counterBox} p-4 flex flex-col justify-end text-primary bg-white`}
				>
					<p className="font-black text-xl">{card.value ?? 0}</p>
					<p className="text-sm">{card.label}</p>
				</div>
			))}
		</div>
	);
}

function ReportTableShell({ title, children, maxHeight = '320px' }) {
	return (
		<div className="border border-slate-200 rounded-md bg-white overflow-hidden flex flex-col min-h-0">
			<div className="px-3 py-2 text-sm font-black text-primary border-b border-slate-200 bg-sky-100">
				{title}
			</div>
			<div className="overflow-auto" style={{ maxHeight }}>
				{children}
			</div>
		</div>
	);
}

function StuckFilesTable({ data }) {
	const rows = Array.isArray(data) ? data : [];
	const total = rows.reduce((sum, row) => sum + (Number(row.count) || 0), 0);

	return (
		<ReportTableShell title="File stuck in processing for more than past 24 hrs">
			<table className="w-full text-sm">
				<thead className="sticky top-0 bg-sky-50">
					<tr className="text-left text-primary">
						<th className="px-3 py-2 font-semibold">Vendor</th>
						<th className="px-3 py-2 font-semibold text-right">No. of Invoices</th>
					</tr>
				</thead>
				<tbody>
					{rows.length === 0 ? (
						<tr>
							<td colSpan={2} className="px-3 py-4 text-center text-slate-400">
								No data
							</td>
						</tr>
					) : (
						rows.map((row, index) => (
							<tr
								key={`${row.vendor_name}-${index}`}
								className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}
							>
								<td className="px-3 py-2">{displayName(row.vendor_name)}</td>
								<td className="px-3 py-2 text-right">{row.count ?? 0}</td>
							</tr>
						))
					)}
				</tbody>
				{rows.length > 0 && (
					<tfoot>
						<tr className="border-t border-slate-200 font-semibold bg-sky-50">
							<td className="px-3 py-2">Total</td>
							<td className="px-3 py-2 text-right">{total}</td>
						</tr>
					</tfoot>
				)}
			</table>
		</ReportTableShell>
	);
}

function OverallVendorStatusTable({ data, handleNavigate, handleNavigateToContracts }) {
	const extraColumns = useMemo(()=> {
		if (data.length > 0){
			let ret = []
			for (const item of data){
				
				for (const k of Object.keys(item.val)){
					ret.push(k)
				}
			}
			let lst = [...new Set(ret)]
			const priority = ["invoice", "contract"];

			const result = [
			...lst.filter(item => priority.includes(item.toLowerCase())),
			...lst.filter(item => !priority.includes(item.toLowerCase()))
			]
			return result
		}
		return []

	}, [data])
	function capitalizeFirstLetter(value) {
		if (typeof value !== "string") {
			return "";
		}

		if (!value.trim()) {
			return "";
		}

		return value.charAt(0).toUpperCase() + value.slice(1);
		}


	return (
		<ReportTableShell title="Overall Vendor Status" maxHeight="360px">
			<table className="w-full text-sm">
				<thead className="sticky top-0 bg-sky-50">
					<tr className="text-left text-primary">
						<th className="px-3 py-2 font-semibold">Expense Area</th>
						<th className="px-3 py-2 font-semibold">Client</th>
						<th className="px-3 py-2 font-semibold">Vendor</th>
						{/* <th className="px-3 py-2 font-semibold text-right">Invoice</th>
						<th className="px-3 py-2 font-semibold text-right">Contract</th> */}
						{extraColumns.map(col => 
							<th className="px-3 py-2 font-semibold text-right">{capitalizeFirstLetter(col)}</th>
						)}
						
					</tr>
				</thead>
				<tbody>
					{data.length === 0 ? (
						<tr>
							<td colSpan={6} className="px-3 py-4 text-center text-slate-400">
								No data
							</td>
						</tr>
					) : (
						data.map((row, index) => (
							<tr
								key={`${row.category_name}-${row.client_name}-${row.vendor_name}-${index}`}
								className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}
							>
								<td className="px-3 py-2">{row.key.category_name}</td>
								<td className="px-3 py-2">{row.key.client_name}</td>
								<td className="px-3 py-2">{row.key.vendor_name}</td>
								{/* <td className="px-3 py-2 text-right">{row.invoice}</td>
								<td className="px-3 py-2 text-right">{row.contract}</td> */}
								{extraColumns.map(col => {
									if (col.toLowerCase() === 'invoice') {
										const count = row.val?.invoice || 0;
										const isLink = count > 0;
										return (
											<td
												onClick={isLink ? () => handleNavigate(row.key) : undefined}
												className={`px-3 py-2 text-right${isLink ? ' cursor-pointer text-primary hover:underline hover:bg-sky-50' : ''}`}
											>
												{count}
											</td>
										);
									} else if (col.toLowerCase() === 'contract') {
										const count = row.val?.contract || 0;
										const isLink = count > 0;
										return (
											<td
												onClick={isLink ? () => handleNavigateToContracts(row.key) : undefined}
												className={`px-3 py-2 text-right${isLink ? ' cursor-pointer text-primary hover:underline hover:bg-sky-50' : ''}`}
											>
												{count}
											</td>
										);
									} else {
										return <td className="px-3 py-2 text-right">{row.val?.[col] || 0}</td>
									}
								})}
							</tr>
						))
					)}
				</tbody>
			</table>
		</ReportTableShell>
	);
}

function InvoiceStatusTable({ data }) {
	const rows = Array.isArray(data) ? data : [];

	return (
		<ReportTableShell title="Invoice Status" maxHeight="360px">
			<table className="w-full text-sm">
				<thead className="sticky top-0 bg-sky-50">
					<tr className="text-left text-primary">
						<th className="px-3 py-2 font-semibold">Expense Area</th>
						<th className="px-3 py-2 font-semibold">Client</th>
						<th className="px-3 py-2 font-semibold">Vendor</th>
						<th className="px-3 py-2 font-semibold">Status</th>
						<th className="px-3 py-2 font-semibold">Next Status</th>
					</tr>
				</thead>
				<tbody>
					{rows.length === 0 ? (
						<tr>
							<td colSpan={5} className="px-3 py-4 text-center text-slate-400">
								No data
							</td>
						</tr>
					) : (
						rows.map((row, index) => (
							<tr
								key={`${row.client_name}-${row.vendor_name}-${row.disp_status}-${index}`}
								className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}
							>
								<td className="px-3 py-2">{displayName(row.category_name)}</td>
								<td className="px-3 py-2">{displayName(row.client_name)}</td>
								<td className="px-3 py-2">{displayName(row.vendor_name)}</td>
								<td className="px-3 py-2">{displayName(row.disp_status)}</td>
								<td className="px-3 py-2">{row.next_status || ''}</td>
							</tr>
						))
					)}
				</tbody>
			</table>
		</ReportTableShell>
	);
}

function VendorsByStatusPie({ data }) {
	const merged = useMemo(() => mergeStatusCounts(data), [data]);
	const labels = Object.keys(merged);
	const values = Object.values(merged);
	const total = values.reduce((sum, n) => sum + n, 0);

	const chartData = {
		labels,
		datasets: [
			{
				data: values,
				backgroundColor: labels.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
				borderWidth: 1,
				borderColor: '#fff',
			},
		],
	};

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				position: 'right',
				labels: { boxWidth: 12, font: { size: 11 } },
			},
			tooltip: {
				callbacks: {
					label: (ctx) => {
						const value = ctx.raw || 0;
						const pct = total ? ((value / total) * 100).toFixed(0) : 0;
						return `${ctx.label}: ${value} (${pct}%)`;
					},
				},
			},
		},
	};

	return (
		<div className="border border-slate-200 rounded-md bg-white p-3">
			<p className="text-sm font-black text-primary mb-2">
				Count of Vendors by Status
			</p>
			<div className="h-64">
				{labels.length === 0 ? (
					<p className="text-center text-slate-400 pt-16">No data</p>
				) : (
					<Pie data={chartData} options={options} />
				)}
			</div>
		</div>
	);
}

function InvoicesByStatusBar({ data }) {
	const merged = useMemo(() => mergeStatusCounts(data), [data]);
	const labels = Object.keys(merged);
	const values = Object.values(merged);

	const chartData = {
		labels,
		datasets: [
			{
				label: 'Invoices',
				data: values,
				backgroundColor: '#4472C4',
				borderRadius: 2,
			},
		],
	};

	const options = {
		indexAxis: 'y',
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
		},
		scales: {
			x: {
				beginAtZero: true,
				title: { display: true, text: 'Invoices', font: { size: 11 } },
				ticks: { precision: 0 },
			},
			y: {
				title: { display: true, text: 'Status', font: { size: 11 } },
				ticks: { font: { size: 11 } },
			},
		},
	};

	return (
		<div className="border border-slate-200 rounded-md bg-white p-3">
			<p className="text-sm font-black text-primary mb-2">
				Count of Invoices by Status
			</p>
			<div className="h-64">
				{labels.length === 0 ? (
					<p className="text-center text-slate-400 pt-16">No data</p>
				) : (
					<Bar data={chartData} options={options} />
				)}
			</div>
		</div>
	);
}
